
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Base URL for your API
const BaseUrl = process.env.REACT_APP_API_URL;
// const BaseUrl = "http://192.168.88.203:5000";

// --- Asynchronous Thunks ---

// ✅ Version Thunk
export const fetchVersions = createAsyncThunk(
    'TaskMaster/fetchVersions',
    async (token, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/version_view`,);
            return response.data?.version || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Add Version Thunk
export const addVersion = createAsyncThunk(
    'version/addVersion',
    async ({ values }, { rejectWithValue }) => {
        try {
            const selectedDate = values.VersionDate;

            const getCurrentTime = () => {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                return `${hours}:${minutes}:${seconds}`;
            };
            const currentTime = getCurrentTime();
            const versionDateWithTime = `${selectedDate} ${currentTime}`;

            const versionData = {
                Project: values.Project,
                Version: values.Version,
                VersionDate: versionDateWithTime,
            };
            const response = await axios.post(`${BaseUrl}/version_add/${values.Project}`, versionData,);
            if (response.data.success) {
                return response.data;
            } else {
                return rejectWithValue(response.data.message || "Failed to add version");
            }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.response?.data?.error ||
                "An error occurred while adding version"
            );
        }
    }
);

// Update Version Thunk
export const updateVersion = createAsyncThunk(
    'version/updateVersion',
    async ({ id, versionData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${BaseUrl}/version_update/${id}`, versionData,);

            if (response.data.success === true) {

                return response.data.version;
            } else {
                return rejectWithValue(response.data.message || "Failed to update version");
            }
        } catch (err) {
            return rejectWithValue(

                err.response?.data?.message || err.message || "Error updating version"
            );
        }
    }
);

// Delete Version Thunk
export const DeleteVersion = createAsyncThunk(
    'version/DeleteVersion',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/version_delete/${id}`);

            if (response.data.success) {
                return id; // return id to remove from Redux state
            } else {
                return rejectWithValue(response.data.message || "Failed to delete version");
            }
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || err.message || "An error occurred"
            );
        }
    }
);


// --- Redux Slice ---
const VersionSlice = createSlice({
    name: 'version',
    initialState: {

        // Version Data State
        VersionData: [],
        versionsLoading: false,
        versionsError: null,

        // Add version specific states
        addVersionLoading: false,
        addVersionError: null,
        addVersionSuccessMessage: null,

        // Delete version specific states
        deleteVersionLoading: false,
        deleteVersionError: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder

            // --- Version Data ---
            .addCase(fetchVersions.pending, (state) => {
                state.versionsLoading = true;
                state.versionsError = null;
            })
            .addCase(fetchVersions.fulfilled, (state, action) => {
                state.versionsLoading = false;
                state.VersionData = action.payload;
            })
            .addCase(fetchVersions.rejected, (state, action) => {
                state.versionsLoading = false;
                state.versionsError = action.payload;
            })


            // --- Add Version ---
            .addCase(addVersion.pending, (state) => {
                state.versionsLoading = true;
                state.versionsError = null;
            })
            .addCase(addVersion.fulfilled, (state, action) => {
                state.versionsLoading = false;
                // Optional: Push the newly added version into VersionData
                state.VersionData.push(action.payload); // Make sure payload structure is as expected
            })
            .addCase(addVersion.rejected, (state, action) => {
                state.versionsLoading = false;
                state.versionsError = action.payload;
            })

            // --- Update Version ---
            .addCase(updateVersion.pending, (state) => {
                state.versionsLoading = true;
                state.versionsError = null;
            })
            .addCase(updateVersion.fulfilled, (state, action) => {
                state.versionsLoading = false;
                const updatedVersion = action.payload;
                const index = state.VersionData.findIndex(v => v._id === updatedVersion._id);
                if (index !== -1) {
                    state.VersionData[index] = updatedVersion;
                }
            })
            .addCase(updateVersion.rejected, (state, action) => {
                state.versionsLoading = false;
                state.versionsError = action.payload;
            })

            // --- Delete Version ---
            .addCase(DeleteVersion.pending, (state) => {
                state.deleteVersionLoading = true;
                state.deleteVersionError = null;
            })
            .addCase(DeleteVersion.fulfilled, (state, action) => {
                state.deleteVersionLoading = false;
                state.VersionData = state.VersionData.filter(
                    (version) => version._id !== action.payload
                );
            })
            .addCase(DeleteVersion.rejected, (state, action) => {
                state.deleteVersionLoading = false;
                state.deleteVersionError = action.payload;
            });
    }
});

// Export the reducer
export default VersionSlice.reducer;